import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { any, is, propEq, takeLast } from 'ramda'
import { Menu, Layout } from 'antd'
import cx from 'classnames'
import styled from 'styled-components'
import * as Icons from '@icons'
import useAuth from '@lib/auth'
import { menuIcons } from '@lib/constants'
import useCompanyUrl from '@lib/hooks/useCompanyUrl'
import useWebSystemRole from '@lib/hooks/useWebSystemRole'
import envStore from '@/env'
import useSimpleMode from '@/hooks/useSimpleMode'
import useViewport from '@/hooks/useViewport'
import {
  useRoutes,
  Route,
  RouteItem,
  STATIONARY_COMBUSTION_MANAGEMENT,
  ADMIN_COMPANY_LIST_MANAGEMENT,
  SIMPLE_MODE_ENTRANCE,
} from '@/routes'
import history from '@/routes/history'
import { useProfile } from '@/services/profile'
import { getSideMenuHiddenAuthUrls } from 'src/containers/WebSettingV2/useTabsConfig'
import usePageGhgQuery from 'src/hooks/usePageGhgQuery'
import { useTranslationConfig } from 'src/hooks/useTranslationConfig'
import * as routes from 'src/routes'
import NodePathIcon from './NodePathIcon'

const { has } = require('ramda')

const style = { height: '100%', borderRight: 0 }

const sideMenuHiddenAuthUrls = getSideMenuHiddenAuthUrls()

const isHiddenSideMenuPath = (path?: string) => !!path && sideMenuHiddenAuthUrls.has(path)

const filterSideMenuRoutes = (routes: Route[]): Route[] =>
  routes
    .map(route => {
      if (!route.children?.length) {
        return route
      }

      return {
        ...route,
        children: route.children.filter(child => !isHiddenSideMenuPath(child.path)),
      }
    })
    .filter(route => {
      if (isHiddenSideMenuPath(route.path)) {
        return false
      }

      if (route.isSubMenu && route.children?.length) {
        return any(propEq('readable', true))(route.children as any)
      }

      return true
    })

function SideMenu() {
  const { t } = useTranslation()

  const location = useLocation()

  const { isMobile } = useViewport()

  const { _routes } = useRoutes(routes)
  const menuRoutes = React.useMemo(() => filterSideMenuRoutes(_routes), [_routes])

  const { profile } = useProfile()

  const companyUrl = useCompanyUrl()

  const systemRole = useWebSystemRole()

  const { user } = useAuth()

  const { isGHGProtocol } = usePageGhgQuery()

  const { isUsedSimpleMode } = useSimpleMode()

  const [defaultOpenKey, pathname] = location.pathname.split('/').slice(1)

  const [openKeys, setOpenKeys] = React.useState([])

  const handleOpenChange = (openKeys: React.Key[]) => {
    setOpenKeys(takeLast(1, openKeys) as any) // ramda對ts的支援似乎還不夠好...型別會錯
  }

  const handleTitleClick = (evt: React.MouseEvent<HTMLElement>) => {
    // handleModeChange(AlarmModes.ALL)()
    // setRectangle(null)
    history.push(`${evt.currentTarget.dataset.path}`)
  }

  const handleLogoClick = () => {
    // handleModeChange(AlarmModes.ALL)()
    // setRectangle(null)
    if (systemRole === 'Company') {
      history.push(companyUrl.urlReplacer(STATIONARY_COMBUSTION_MANAGEMENT))
    } else {
      history.push(ADMIN_COMPANY_LIST_MANAGEMENT)
    }
  }

  const renderIcon = (item: RouteItem) => {
    if (!item.mainKey) {
      return
    }
    // icon 設定
    let Icon: any = menuIcons[item.mainKey]

    if (is(String, Icon) && has([Icon])(Icons)) {
      const Component = (Icons as any)[Icon]
      Icon = <Component />
    }
    if (is(Function, Icon)) {
      Icon = <Icon />
    }

    return Icon
  }

  const renderMenuItem = (item: RouteItem) => {
    if (!item.readable) {
      return
    }

    const to = companyUrl.urlReplacer(item.path)
    const className = item.isSubMenuIcon ? { className: 'flex items-center' } : {}

    return (
      <MenuItem key={item.id} disabled={item.disabled}>
        <span {...className}>
          {item.isSubMenuIcon && <NodePathIcon />}
          <Link to={to}>{item.title}</Link>
        </span>
      </MenuItem>
    )
  }

  const renderMenu = (data: any[]) =>
    data.map((item: Route) => {
      const hasReadableChild = any(propEq('readable', true))((item.children as any) || [])
      if (!item.readable && !hasReadableChild) {
        return []
      }

      if (!item.isSubMenu) {
        const to = companyUrl.urlReplacer(item.path)

        return (
          <MainMenu
            key={item.id}
            className={cx({ 'pl-16': !isMobile })}
            icon={renderIcon(item)}
            disabled={item.disabled}>
            <Link to={to}>{item.title}</Link>
          </MainMenu>
        )
      }

      return item.children ? (
        <SubMenu
          key={item.id}
          title={
            <>
              {renderIcon(item)}
              {!isMobile && <span>{item.title}</span>}
            </>
          }
          disabled={item.disabled}>
          {item.children.map(renderMenuItem)}
        </SubMenu>
      ) : (
        <li
          key={item.id}
          data-path={item.path}
          onClick={item.disabled ? () => {} : handleTitleClick}
          className={
            item.disabled
              ? 'ant-menu-submenu ant-menu-submenu-inline ant-menu-submenu-selected ant-menu-submenu-disabled'
              : 'ant-menu-submenu ant-menu-submenu-inline ant-menu-submenu-selected'
          }>
          <div className="ant-menu-submenu-title">
            {renderIcon(item)}
            <span className="inline-flex">{item.title}</span>
          </div>
        </li>
      )
    })

  const { currentLang } = useTranslationConfig()

  const googleTranslateElementInit = React.useCallback(() => {
    // @ts-ignore
    new window.google.translate.TranslateElement(
      {
        pageLanguage: currentLang,
        includedLanguages: 'zh-TW,zh-CN,en,th',
      },
      'google_translate_element'
    )
  }, [currentLang])

  React.useEffect(() => {
    if (!envStore.isGoogleTranslateEnable) {
      return
    }

    var addScript = document.createElement('script')
    addScript.setAttribute(
      'src',
      // '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
      `${envStore.publicUrl}/js/google_translate_element.js`
    )
    document.body.appendChild(addScript)
    // @ts-ignore
    window.googleTranslateElementInit = googleTranslateElementInit
  }, [googleTranslateElementInit])

  return (
    <Sider collapsed={isMobile} width={isGHGProtocol ? 200 : 160}>
      <Logo onClick={handleLogoClick} className="cursor-pointer">
        {/* <img src={`${publicUrl}/temp-logo@3x.png`} alt={profile.name} /> */}
        {!!profile && (
          <>
            <img src={profile.url} alt={profile.name} />
            {/* <div className="logo-text">{profile.name}</div> */}
          </>
        )}
      </Logo>
      {envStore.isGoogleTranslateEnable && <div id="google_translate_element"></div>}
      <StyledMenu
        mode="inline"
        openKeys={openKeys}
        defaultSelectedKeys={[pathname || defaultOpenKey]}
        defaultOpenKeys={[defaultOpenKey]}
        onOpenChange={handleOpenChange}
        style={style}>
        {isUsedSimpleMode && (
          <MainMenu
            key="shortcut"
            className={cx({ 'pl-16': !isMobile })}
            icon={<Icons.IconRocket />}>
            <Link to={companyUrl.urlReplacer(SIMPLE_MODE_ENTRANCE)}>快速登打</Link>
          </MainMenu>
        )}
        {renderMenu(menuRoutes)}
      </StyledMenu>
    </Sider>
  )
}

const Sider = styled(Layout.Sider)`
  #google_translate_element {
    display: flex;
    justify-content: center;
  }

  box-shadow: ${p => p.theme.menu.boxShadow};

  &.ant-layout-sider {
    background-color: ${p => p.theme.menu.bg};
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100vh;
  }

  .ant-skeleton-title {
    margin-left: 16px;
  }

  .ant-skeleton-content .ant-skeleton-paragraph > li + li {
    margin-top: 28px;
  }
`

const StyledMenu = styled(Menu)`
  &.ant-menu {
    height: auto !important;
    margin-top: 0px;
    margin-bottom: auto;
    background-color: ${p => p.theme.menu.bg};
  }

  &.ant-menu-inline-collapsed {
    .ant-menu-item {
      > a {
        float: right;

        .anticon {
          vertical-align: middle;
          margin-right: 0px;
        }
      }
    }
  }

  .ant-menu-submenu-title {
    color: ${p => p.theme.menu.main.text};
    font-size: 25px;
    font-weight: bold;

    svg {
    }
  }

  .ant-menu-submenu-title:hover {
    color: ${p => p.theme.menu.main.hover.text};
    background: ${p => p.theme.menu.main.hover.bg};
  }

  &.ant-menu-inline {
    .ant-menu-item:not(:last-child) {
      margin-bottom: 0px;
    }

    .ant-menu-submenu > .ant-menu-submenu-title {
      height: auto;
    }

    .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow {
      right: 10px;
      top: 47%;
    }

    .ant-menu-inline
      .ant-menu-submenu-inline
      > .ant-menu-submenu-title
      .ant-menu-submenu-arrow::after {
      height: 1px;
      background: ${p => p.theme.menu.text};
    }

    .ant-menu-submenu-open.ant-menu-submenu-inline
      > .ant-menu-submenu-title
      .ant-menu-submenu-arrow {
      top: 43%;
    }

    .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::after {
      transform: rotate(45deg) translateX(-3px);
      width: 7px;
      height: 1px;
    }

    .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::before {
      transform: rotate(-45deg) translateX(3px);
      width: 7px;
      height: 1px;
    }

    .ant-menu-submenu-open.ant-menu-submenu-inline
      > .ant-menu-submenu-title
      .ant-menu-submenu-arrow::before {
      transform: rotate(45deg) translateX(3px);
    }

    .ant-menu-submenu-open.ant-menu-submenu-inline
      > .ant-menu-submenu-title
      .ant-menu-submenu-arrow::after {
      transform: rotate(-45deg) translateX(-3px);
    }

    .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::after,
    .ant-menu-submenu-inline > .ant-menu-submenu-title .ant-menu-submenu-arrow::before {
      background: ${p => p.theme.menu.text};
    }

    .ant-menu-submenu-inline > .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::before,
    .ant-menu-submenu-inline > .ant-menu-submenu-title:hover .ant-menu-submenu-arrow::after {
      background: ${p => p.theme.menu.text};
    }

    .ant-menu-submenu-title {
      margin-top: 6px;
      font-size: 14px;

      @media only screen and (max-width: 700px) {
        padding-left: 16px !important;
      }

      @media only screen and (min-width: 701px) {
        white-space: normal;
        height: auto;
        padding: 8px 0px 8px 16px !important;
        line-height: normal;
      }

      > span {
        display: flex;
        align-items: center;
      }

      > span span {
        max-width: 80px !important;
        margin-left: 6px;
      }
    }
  }
`

const SubMenu = styled(Menu.SubMenu)`
  color: ${p => p.theme.menu.main.text};

  &.ant-menu-submenu > .ant-menu:not(:empty) {
    padding-bottom: 20px;
    background-color: ${p => p.theme.menu.bg};
  }

  .ant-menu-sub.ant-menu-inline > .ant-menu-item {
    @media only screen and (max-width: 700px) {
      height: 36px;
      line-height: 36px;
    }

    @media only screen and (min-width: 701px) {
      min-height: 36px;
      line-height: normal;
      height: auto;
      white-space: normal;
      padding: 8px 0px;
    }
  }

  &.ant-menu-submenu-vertical > .ant-menu-submenu-title {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const MainMenu = styled(Menu.Item)`
  &.ant-menu-item {
    background-color: ${p => p.theme.menu.bg};

    a {
      font-size: 14px;
      font-weight: bold;
      color: ${p => p.theme.menu.main.text};
    }

    .ant-menu-title-content {
      margin-left: 6px;
    }

    .anticon {
      font-size: 20px;
      color: ${p => p.theme.menu.main.text};
      :hover {
        svg {
          fill: ${p => p.theme.menu.main.text}; !important;
        }
      }
    }

    @media only screen and (max-width: 700px) {
      height: 40px;
      line-height: 40px;
    }

    @media only screen and (min-width: 701px) {
      min-height: 40px;
      height: auto;
      line-height: normal;
      white-space: normal;
      padding: 8px 0px;
    }
  }

  &.ant-menu-item-selected {
    background: ${p => p.theme.menu.main.selected.bg} !important;
    &:after {
      display: none;
    }

    a {
      color: ${p => p.theme.menu.main.selected.text} !important;
      font-weight: bold;
    }

    .anticon {
      color: ${p => p.theme.menu.main.selected.text} !important;
      :hover {
        svg {
          fill: ${p => p.theme.menu.main.selected.text} !important;
        }
      }
    }

    svg.style-icon-approval,
    .style-icon-cate6,
    .style-icon-history {
      path {
        stroke: ${p => p.theme.menu.main.selected.text} !important;
      }
    }

    svg.style-icon-company,
    .style-icon-user,
    .style-icon-static {
      g path {
        fill: ${p => p.theme.menu.main.selected.text} !important;
      }
    }
    .style-icon-evaluate,
    .style-icon-company-list {
      path {
        fill: ${p => p.theme.menu.main.selected.text} !important;
      }
    }
  }

  &.ant-menu-item:not(.ant-menu-item-selected):hover {
    background: ${p => p.theme.menu.main.hover.bg} !important;

    a {
      color: ${p => p.theme.menu.main.hover.text} !important;
    }

    svg.ant-menu-item-icon {
    }
  }
`

const MenuItem = styled(Menu.Item)`
  &.ant-menu-item-selected {
    background: transparent !important;
    position: relative;
    z-index: 2;
    &:after {
      display: none;
    }
    &:before {
      position: absolute;
      display: block;
      content: ' ';
      width: 100%;
      height: 100%;
      background-color: ${p => p.theme.menu.sub.selected.bg};
      left: 0;
      z-index: -1;
    }

    a {
      color: ${p => p.theme.menu.sub.selected.text} !important;
    }
  }

  &.ant-menu-item {
    padding-left: 37px !important;
    text- &:active {
    }

    :hover {
      background-color: ${p => p.theme.menu.sub.hover.bg};
    }

    > span a {
      color: ${p => p.theme.menu.sub.text};
      font-weight: normal;

      &:hover {
        color: ${p => p.theme.menu.sub.hover.text};
      }
    }
  }

  &.ant-menu-item-selected {
    background-color: ${p => p.theme.menu.sub.selected.bg} !important;
  }
`

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px 8px 0px 8px;
  margin-bottom: 0px;
  text-align: center;
  font-weight: 500;

  .logo-text {
    color: #201817;
    font-size: 13px;
  }

  img {
    width: 100%;
    height: auto;
    max-width: 127px;
    margin-bottom: 2px;
  }
`

export default SideMenu
